import React from "react";
import Chart from "react-apexcharts";
import { MoreHorizontal } from "lucide-react"; // 3-dot icon

const getColor = (value) => {
  if (value > 95) return "#009F52";   // Green
  if (value > 90 && value <= 95) return "#FFC100";  // Yellow
  return "#FB0301";
}

const radialOptions = (value) => ({
  chart: { type: "radialBar", sparkline: { enabled: true } },
  plotOptions: {
    radialBar: {
      startAngle: 0,
      endAngle: 360,
      hollow: { size: "65%" },
      track: { background: "#f3f3f3" },
      dataLabels: { show: false },
    },
     states: {
    normal: {
      filter: { type: "none" },
    },
    hover: {
      filter: { type: "none" },
    },
    active: {
      filter: { type: "none" },
    },
  },
  },
  stroke: { lineCap: "round" },
  fill: { type: "solid" },
  colors: [getColor(value)],
});

const Charts = ({ scores, period = "Current" }) => {
  // --- Safe API handling ---
  const isContainer =
    scores &&
    typeof scores === "object" &&
(scores.Current || scores.Previous)
  const data = isContainer ? scores[period] : scores;

  if (!data) {
    return <div>Loading or no data available.</div>;
  }

  // --- Safe number parsing ---
const safeValue = (val) => {
  if (val === null || val === undefined) return "N/A";
  if (val === "N/A") return "N/A";

  const num = Number(val);
  if (Number.isNaN(num)) return "N/A";

  return parseFloat(num.toFixed(2));
};

  // --- Overall score fallback ---
  const rawOverall = data.totalScore ?? data["Overall DQ Score"] ?? 0;
  const overallScore = safeValue(rawOverall);

  // --- KPI list from API ---
  const kpiList = [
    { name: "Freshness", value: safeValue(data.freshness) },
    { name: "Validity", value: safeValue(data.validity) },
    { name: "Uniqueness", value: safeValue(data.uniqueness) },
    { name: "Completeness", value: safeValue(data.completeness) },
    { name: "Consistency", value: safeValue(data.consistency) },
        { name: "Accuracy", value: safeValue(data.accuracy) },


  ];

  const barColors = ["#8A74F9", "#0B63A8"];

  return (
    <div className=" bg-white pt-[20px] pb-[20px]">
      <div className="w-[90%] m-auto">
        <div className="flex flex-wrap items-start gap-6 items-stretch">
          {/* LEFT CARD - Overall */}
          <div className="w-1/4 min-w-[280px]  border border-[#818181] rounded-lg bg-white shadow-sm rounded-[9px]">
            <div className="flex items-center gap-[11px] px-4 py-2 border-b border-[#818181]">
                            <div class="w-[38px] h-[38px] rounded-[5px] bg-[#E8E8EB]"></div>
              <h3 className="text-lg font-semibold">Overall DQ Score</h3>

            </div>

            <div
              className="relative flex items-center justify-center"
              style={{ height: 220 }}
            >
              <Chart
                options={radialOptions(overallScore)}
                series={[overallScore]}
                type="radialBar"
                height={220}
              />
              <div className="absolute flex flex-col items-center">
                <div className="text-4xl font-extrabold leading-none">
                  {/* {Math.round(overallScore)}% */}
                    {overallScore.toFixed(2)}%

                </div>
                <div className="text-sm text-gray-500 mt-1">(0 to 100)</div>
              </div>
            </div>
          </div>

          
   <div className="w-1/6 flex flex-col gap-10 justify-between py-6">
  {/* Green: >95% */}
  <div className="flex items-center gap-4">
    <div className="w-1 h-[40px] rounded-full bg-[#009F52]" /> {/* green */}
    <div className="whitespace-nowrap">
      <div className="text-sm font-medium text-gray-700">DQ & KPI Score</div>
      <div className="text-xs text-gray-500">&gt;95%</div>
    </div>
  </div>

  {/* Yellow: 90–95% */}
  <div className="flex items-center gap-4">
    <div className="w-1 h-[40px] rounded-full bg-[#FFC100]" /> {/* yellow */}
    <div className="whitespace-nowrap">
      <div className="text-sm font-medium text-gray-700">DQ & KPI Score</div>
      <div className="text-xs text-gray-500">90% – 95%</div>
    </div>
  </div>

  {/* Red: ≤90% */}
  <div className="flex items-center gap-4">
    <div className="w-1 h-[40px] rounded-full bg-[#FB0301]" /> {/* red */}
    <div className="whitespace-nowrap">
      <div className="text-sm font-medium text-gray-700">DQ & KPI Score</div>
      <div className="text-xs text-gray-500">&lt;=90%</div>
    </div>
  </div>
</div>


          {/* RIGHT CARD - KPI bars */}
          <div className="flex-1 bg-white shadow-sm rounded-lg rounded-[9px] border border-[#818181]">
            {/* header */}
            <div
              className="flex items-center gap-[11px] px-4 py-2 "
style={{ borderBottom: "1px solid #818181" }}
            >
                                          <div class="w-[38px] h-[38px] rounded-[5px] bg-[#E8E8EB]"></div>
              <h3 className="text-lg font-semibold">DQ KPI Score</h3>
              {/* border-b border-[#818181] <MoreHorizontal size={18} className="text-gray-400" /> */}
            </div>

            {/* KPI bars */}
            <div
              className="p-4 space-y-4"
            >
             {kpiList.map((kpi, idx) => {
  const pct = safeValue(kpi.value);
  const isNA = pct === "N/A" || pct === null || pct === undefined;
  const fillColor = !isNA ? getColor(pct) : "#E0E0E0"; // light gray when N/A

  return (
    <div key={idx} className="flex items-center gap-4">
      <div className="flex-1">
        <div className="relative h-6 bg-gray-200 overflow-hidden">
          {!isNA && (
            <div
              className="h-6"
              style={{
                width: `${pct}%`,
                background: fillColor,
                transition: "width 700ms ease",
              }}
            />
          )}
        </div>
      </div>

      <div className="w-16 text-right text-lg font-bold text-gray-800">
        {isNA ? "N/A" : `${pct}%`}
      </div>

      <div className="w-40 text-sm font-medium text-gray-700">
        {kpi.name}
      </div>
    </div>
  );
})}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Charts;
